import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import FadingSpinner from "../../common/FadingSpinner/FadingSpinner";
import {
  approveTimesheetFromEmail,
  getSingleTimesheetFromEmail
} from "../helpers/fontApi";
import { UpdateStatus } from "../helpers/enum";
import { calculateTimesheetStatus } from "../helpers/helpers";
import { checkIfFeedbackCached } from "../../../helpers/feedback";
import TimesheetFeedback from "../../timesheetFeedback";

const TimesheetApprove = ({ tokenStore }) => {
  const { timesheetRef } = useParams();
  const [updateState, setUpdateState] = useState(null);
  const [approvedBefore, setApprovedBefore] = useState(false);
  const [timesheet, setTimesheet] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const user = tokenStore.getUser();
  const userEmail = user.emails[0];
  const [loading, setLoading] = useState(true); // New loading state

  useEffect(() => {
    setUpdateState(UpdateStatus.Loading);
    if (!loading) {
      let isMounted = true; // Flag to track component mount status

      if (!dataFetched) {
        const fetchDataAsync = async () => {
          try {
            const response = await getSingleTimesheetFromEmail(timesheetRef);

            if (isMounted) {
              setTimesheet(response);
              const status = calculateTimesheetStatus(response);

              switch (status) {
                case "Pending":
                case "Rejected":
                  await approveTimesheetFromEmail(
                    timesheetRef,
                    setUpdateState,
                    userEmail
                  );
                  setDataFetched(true);
                  break;
                case "Approved":
                  setApprovedBefore(true);
                  setUpdateState(UpdateStatus.Success);
                  break;
                default:
                  setUpdateState(UpdateStatus.ErrorApprove);
              }
            }
          } catch (error) {
            console.error(error);
            setUpdateState(UpdateStatus.ErrorApprove);
          }
        };

        fetchDataAsync();
      }

      // Cleanup function to handle unmounting
      return () => {
        isMounted = false; // Update component mount status on unmount
      };
    }
    return undefined;
  }, [dataFetched, timesheetRef, userEmail, loading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000); // Simulated loading time to avoid flickering
    return () => clearTimeout(timer);
  }, []);

  const handleReload = () => window.location.reload();

  if (updateState === UpdateStatus.Loading) {
    return (
      <div style={{ marginBottom: "10rem" }}>
        <div className="timesheet__notification-wrapper">
          <div className="timesheet__notification">
            <FadingSpinner />
            <h3 className="timesheet__notification-title tw-text-2xl tw-font-bold">
              This won't take too long
            </h3>
          </div>
        </div>
      </div>
    );
  }
  if (updateState === UpdateStatus.Success) {
    if (approvedBefore) {
      return (
        <div style={{ marginBottom: "10rem" }}>
          <div className="mx-auto text-center">
            <div className="alert-success" role="alert">
              <strong>Timesheet was approved before!</strong>
            </div>
            <div className="form-group tw-mt-2">
              <Link
                to="/timesheets"
                className="btn btn-primary btn-lg"
                type="button"
              >
                Go to Timesheets
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        style={{ marginBottom: checkIfFeedbackCached() ? "28rem" : "50rem" }}
      >
        <div className="timesheet__notification-wrapper">
          <div className="timesheet__notification tw-mr-3">
            <img
              src="https://hrgo.spacebetween.co.uk/images/icons/timesheet-approve.svg"
              alt="timesheet-approve"
            />
            <h2 className="timesheet__notification-title text-center tw-font-bold tw-text-3xl tw-pb-2 ">
              Timesheet approved
            </h2>
            <b className="text-center">
              Your approval of the timesheet has been submitted successfully.
            </b>
            <b>Thank you.</b>
            {!checkIfFeedbackCached() && (
              <TimesheetFeedback
                tokenStore={tokenStore}
                candidateRef={timesheet.candidateRef}
                candidateName={timesheet.candidateName}
                timesheetRef={timesheetRef}
              />
            )}
            <div className="form-group tw-mt-10">
              <Link
                to="/timesheets"
                className="btn btn-primary btn-lg"
                type="button"
              >
                Go to Timesheets
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (updateState === UpdateStatus.ErrorApprove) {
    return (
      <div style={{ marginBottom: "10rem" }}>
        <div className="mx-auto text-center">
          <div className="alert-error" role="alert">
            <strong>Error!</strong>
          </div>
          <div className="form-group">
            <button
              className="btn btn-primary btn-lg"
              type="button"
              onClick={handleReload}
            >
              Try Again
            </button>
          </div>
          <div className="form-group tw-mt-10">
            <Link
              to="/timesheets"
              className="btn btn-secondary btn-lg"
              type="button"
            >
              Go to Timesheets
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default TimesheetApprove;
